import React, { useEffect, useRef, useState } from "react";
import { 
  IoArrowForward, 
  IoCallOutline, 
  IoLocationOutline, 
  IoMailOutline, 
  IoLogoWhatsapp 
} from "react-icons/io5";
import banner1 from "../assets/banner1.jpg";

export const CONTACT_LOCATION = [
  "Kelvin House, Samora Avenue",
  "2nd Floor, Room No. 202",
  "P.O. Box 8215",
  "Dar es Salaam, Tanzania",
];

export const CONTACT_PHONES = [
  { number: "+255 222 130 349", note: "Landline", href: "tel:+255222130349" },
  { number: "+255 756 442 647", note: "Mobile", href: "tel:+255756442647" },
  { number: "+255 787 605 911", note: "Mobile", href: "tel:+255787605911" },
  { number: "+255 712 857 340", note: "Mobile", href: "tel:+255712857340" },
];

export const WHATSAPP_CONTACT = {
  number: "+255 756 442 647",
  href: "https://wa.me/255756442647"
};

export const CONTACT_EMAIL = "info@paclese.com";

/* ------------------------------------------------------------------ */
/* Scroll-reveal utility                                              */
/* ------------------------------------------------------------------ */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ as: Tag = "div", children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */
export default function ContactUsContent() {
  return (
    <section className="mod-contact-page">
      <style>{`
        /* --- Base & Dark Theme (Matching About Page) --- */
        .mod-contact-page {
          background-color: #0f172a; /* Deep Navy Slate */
          color: #f8fafc;
          font-family: system-ui, -apple-system, sans-serif;
          overflow-x: hidden;
          min-height: 100vh;
        }

        /* --- Scroll Reveal Animations --- */
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        .reveal.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* --- Hero Section --- */
        .mod-hero {
          position: relative;
          min-height: 50vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 4rem 1.25rem 3rem; /* Mobile tailored */
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .mod-hero { padding: 6rem 1.5rem 4rem; }
        }
        .mod-hero-bg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }
        .mod-hero-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transform: scale(1.05);
          animation: slowZoom 20s ease-out forwards;
        }
        .mod-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(15, 23, 42, 0.7) 0%, #0f172a 100%);
        }
        .mod-hero-content {
          position: relative;
          z-index: 1;
          max-width: 800px;
        }
        .mod-kicker {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #60a5fa;
          font-weight: 600;
          margin-bottom: 1rem;
          display: block;
        }
        .mod-hero-title {
          font-size: clamp(2.2rem, 6vw, 4rem);
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 1.5rem;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }
        .mod-hero-title em {
          color: #3b82f6; /* Bright Blue */
          font-style: normal;
        }
        .mod-hero-lead {
          font-size: clamp(1rem, 2.5vw, 1.25rem);
          color: #94a3b8;
          line-height: 1.6;
          font-weight: 400;
        }

        /* --- Main Layout --- */
        .mod-section {
          padding: 3rem 1.25rem 6rem; /* Mobile tailored */
          position: relative;
        }
        @media (min-width: 768px) {
          .mod-section { padding: 4rem 1.5rem 8rem; }
        }
        .mod-contact-grid {
          display: block;
          max-width: 1000px;
          margin: 0 auto;
        }

        /* --- Directory --- */
        .mod-card {
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 1.5rem; /* Mobile spacing */
          backdrop-filter: blur(10px);
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem; /* Mobile spacing */
        }
        @media (min-width: 768px) {
           .mod-card {
               grid-template-columns: 1fr 1fr;
               padding: 3rem;
               gap: 3rem;
           }
        }
        
        /* Grid Utilities */
        .mod-col-full { grid-column: 1 / -1; }

        .mod-contact-block {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }
        @media (min-width: 768px) {
          .mod-contact-block { gap: 1.25rem; }
        }

        .mod-contact-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          background: rgba(59, 130, 246, 0.1);
          color: #60a5fa;
          border-radius: 12px;
          border: 1px solid rgba(59, 130, 246, 0.2);
          font-size: 1.35rem;
          flex-shrink: 0;
        }
        @media (min-width: 768px) {
          .mod-contact-icon { width: 48px; height: 48px; font-size: 1.5rem; }
        }

        /* Specialized WhatsApp Icon Styling */
        .mod-contact-icon-wa {
          background: rgba(37, 211, 102, 0.1);
          color: #25d366;
          border-color: rgba(37, 211, 102, 0.2);
        }

        .mod-contact-info {
            width: 100%;
            min-width: 0; /* Prevents flex children from overflowing */
        }
        .mod-contact-info h3 {
          font-size: 1.1rem;
          color: #ffffff;
          margin: 0 0 0.5rem 0;
          font-weight: 600;
        }
        .mod-contact-info address,
        .mod-contact-info p {
          color: #94a3b8;
          font-style: normal;
          line-height: 1.6;
          margin: 0;
          font-size: 0.95rem;
        }
        
        .mod-contact-link {
          color: #94a3b8;
          text-decoration: none;
          display: block;
          margin-bottom: 0.5rem;
          transition: color 0.2s ease;
          word-break: break-word; /* Mobile safe for long emails */
        }
        .mod-contact-link:hover {
          color: #60a5fa;
        }

        /* Responsive Phone Grid */
        .mod-phone-list-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          list-style: none;
          padding: 0;
          margin: 0;
        }
        @media (min-width: 576px) {
          .mod-phone-list-grid { grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
        }

        .mod-phone-link {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          text-decoration: none;
          background: rgba(15, 23, 42, 0.4);
          border: 1px solid rgba(255, 255, 255, 0.05);
          padding: 0.75rem 1rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          width: 100%;
        }
        .mod-phone-link:hover {
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.3);
          transform: translateX(5px);
        }

        .mod-wa-btn-wrap { max-width: 100%; }
        @media (min-width: 768px) {
          .mod-wa-btn-wrap { max-width: 320px; }
        }

        /* Specialized WhatsApp Link Hover */
        .mod-phone-link-wa:hover {
          background: rgba(37, 211, 102, 0.08);
          border-color: rgba(37, 211, 102, 0.3);
        }
        .mod-phone-link-wa:hover .mod-phone-arrow { color: #25d366; }

        .mod-phone-number {
          color: #e2e8f0;
          font-weight: 500;
          font-size: 1rem;
        }
        @media (min-width: 768px) {
          .mod-phone-number { font-size: 1.05rem; }
        }

        .mod-phone-note {
          color: #64748b;
          font-size: 0.8rem;
          margin-left: auto;
        }
        .mod-phone-arrow {
          color: #3b82f6;
          opacity: 0;
          transform: translateX(-10px);
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        
        /* Always show arrows on touch devices/mobile for UX */
        @media (max-width: 767px) {
          .mod-phone-arrow { opacity: 0.5; transform: translateX(0); }
        }
        .mod-phone-link:hover .mod-phone-arrow {
          opacity: 1;
          transform: translateX(0);
        }

        @keyframes slowZoom {
          from { transform: scale(1); }
          to { transform: scale(1.1); }
        }
      `}</style>

      {/* ---------- HERO ---------- */}
      <header className="mod-hero">
        <div className="mod-hero-bg">
          <img src={banner1} alt="PACLESE CARGO operations" className="mod-hero-img" />
          <div className="mod-hero-overlay" />
        </div>
        <div className="container mod-hero-content">
          <Reveal delay={80}>
            <h1 className="mod-hero-title">
              Let's move things <em>forward.</em>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mod-hero-lead">
              Whether you're shipping cargo across East Africa or sourcing
              enterprise printing and corporate supplies, our team is ready to
              build a solution around you.
            </p>
          </Reveal>
        </div>
      </header>

      {/* ---------- MAIN GRID ---------- */}
      <section className="mod-section">
        <div className="container">
          <div className="mod-contact-grid">
            
            <aside className="mod-contact-directory w-100">
              <div className="mod-card">

                {/* --- 1. Phones (Top Full Width) --- */}
                <Reveal delay={80} className="mod-contact-block mod-col-full">
                  
                  <div className="mod-contact-info">
                    <h3>Direct Lines</h3>
                    <ul className="mod-phone-list-grid">
                      {CONTACT_PHONES.map((p) => (
                        <li key={p.href}>
                          <a href={p.href} className="mod-phone-link">
                            <span className="mod-phone-number">{p.number}</span>
                            <span className="mod-phone-note">{p.note}</span>
                            <IoArrowForward className="mod-phone-arrow" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>

                {/* --- 2. WhatsApp (Left Column on Desktop, Stacked on Mobile) --- */}
                <Reveal delay={160} className="mod-contact-block">
                  
                  <div className="mod-contact-info">
                    <h3>WhatsApp</h3>
                    <div className="mod-wa-btn-wrap">
                      <a href={WHATSAPP_CONTACT.href} target="_blank" rel="noopener noreferrer" className="mod-phone-link mod-phone-link-wa">
                        <span className="mod-phone-number">{WHATSAPP_CONTACT.number}</span>
                        <span className="mod-phone-note">Chat with us</span>
                        <IoArrowForward className="mod-phone-arrow" />
                      </a>
                    </div>
                  </div>
                </Reveal>

                {/* --- 3. Email (Right Column on Desktop, Stacked on Mobile) --- */}
                <Reveal delay={240} className="mod-contact-block">
                  <div className="mod-contact-info">
                    <h3>Email</h3>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="mod-contact-link mod-phone-number" style={{ color: '#60a5fa', marginTop: '0.75rem' }}>
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </Reveal>

                {/* --- 4. Head Office (Bottom Left) --- */}
                <Reveal delay={320} className="mod-contact-block">
                  
                  <div className="mod-contact-info">
                    <h3>Head Office</h3>
                    <address>
                      {CONTACT_LOCATION.map((line) => (
                        <span key={line}>
                          {line}
                          <br />
                        </span>
                      ))}
                    </address>
                  </div>
                </Reveal>

                {/* --- 5. Hours (Bottom Right) --- */}
                <Reveal delay={400} className="mod-contact-block">
                  
                  <div className="mod-contact-info">
                    <h3>Business Hours</h3>
                    <p>
                      Monday – Friday · 08:00 – 17:30 EAT
                      <br />
                      Saturday · 09:00 – 13:00 EAT
                    </p>
                  </div>
                </Reveal>

              </div>
            </aside>

          </div>
        </div>
      </section>
    </section>
  );
}